// PO1-3409: texto do aviso "existe, mas está fora do filtro".
//
// Recebe os achados do GET /documentos/localizar (já marcados pelo backend com
// foraEmbarcador / foraTransportadora / foraPeriodo) e monta o que a tela mostra
// no lugar do "nenhum encontrado". Puro, para testar sem montar a tela.
import { labelSituacao } from "../../views/Empresa/ConferenciaFatura/formatters";

export const MAX_LINHAS = 5;

const ehDocFatura = (a) => a.origem === "fatura" || a.origem === "fila_fatura";
const flex = (a, masc, fem) => (ehDocFatura(a) ? fem : masc);

const ehChave = (termo) => String(termo || "").replace(/\D/g, "").length === 44;

export function formatarData(valor) {
  const texto = String(valor || "").trim();
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return texto || null;
}

function descreverDocumento(a) {
  const ehFatura = a.origem === "fatura" || a.origem === "fila_fatura";
  const numero = a.numero != null && a.numero !== "" ? String(a.numero) : null;
  const serie = !ehFatura && a.serie != null && a.serie !== "" ? `/${a.serie}` : "";
  const partes = [`${ehFatura ? "Fatura" : "CT-e"} ${numero ? numero + serie : "(sem número)"}`];
  if (a.transportadora) partes.push(`da ${a.transportadora}`);
  if (a.embarcador) partes.push(`(${a.embarcador})`);
  const data = formatarData(a.dataEmissao);
  if (data) partes.push(`${flex(a, "emitido", "emitida")} em ${data}`);
  return partes.join(" ");
}

function motivosDeFiltro(a) {
  const motivos = [];
  if (a.foraEmbarcador) {
    motivos.push(`é do embarcador ${a.embarcador || "de outra empresa"}: troque a empresa no topo da tela`);
  }
  if (a.foraTransportadora) {
    motivos.push(`é da transportadora ${a.transportadora || "de outro grupo"}, e o topo da tela está filtrando outra transportadora`);
  }
  if (a.foraPeriodo) {
    motivos.push(`foi ${flex(a, "emitido", "emitida")} fora do período selecionado`);
  }
  return motivos;
}

function situacaoTexto(a) {
  if (a.origem === "fatura") return labelSituacao(a.situacaoCodigo, a.situacao);
  return a.situacao || null;
}

export function formatarValor(valor) {
  const n = Number(valor);
  if (valor === null || valor === undefined || valor === "" || Number.isNaN(n)) return null;
  const [inteiro, dec] = n.toFixed(2).split(".");
  return `R$ ${inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`;
}

// PO1-3414: CT-e cancelado na SEFAZ. O CT-e foi apagado do Hub; o que resta é o
// registro do cancelamento. Origem 3 (backfill) só tem a data em que o evento foi
// processado, sem valor nem protocolo.
export const ehCancelado = (a) => a && a.origem === "cancelado";

// Dat_Evento é UTC (o banco devolve com "Z"): a data do aviso é a de Brasília,
// senão um cancelamento depois das 21h sai com o dia seguinte. Brasil sem horário
// de verão desde 2019; conta fixa para não depender de ICU.
export function formatarDataBrt(valor) {
  const texto = String(valor || "").trim();
  if (!/T\d{2}:\d{2}/.test(texto)) return formatarData(texto);
  const d = new Date(texto);
  if (Number.isNaN(d.getTime())) return formatarData(texto);
  const brt = new Date(d.getTime() - 3 * 60 * 60 * 1000);
  const dois = (n) => String(n).padStart(2, "0");
  return `${dois(brt.getUTCDate())}/${dois(brt.getUTCMonth() + 1)}/${brt.getUTCFullYear()}`;
}

function explicarCancelado(a) {
  const quando = formatarDataBrt(a.dataCancelamento);
  const backfill = Number(a.origemRegistro) === 3;
  // Backfill: a data é a do processamento do evento, por isso vai entre parênteses.
  const quandoTxt = quando ? (backfill ? ` (cancelamento recebido em ${quando})` : ` em ${quando}`) : "";
  const partes = [];
  if (a.protocolo) partes.push(`protocolo ${a.protocolo}`);
  const valor = formatarValor(a.valor);
  if (valor) partes.push(valor);
  const detalhe = partes.length ? ` · ${partes.join(" · ")}` : "";
  return `foi cancelado na SEFAZ${quandoTxt}${detalhe}. Ele foi removido do Hub e não pode ser faturado`;
}

// tela: "auditoria" (Auditoria de CT-e) | "integracao" (Integração de Documentos)
function explicar(a, tela) {
  const motivos = motivosDeFiltro(a);
  const situacao = situacaoTexto(a);
  const arquivo = a.nomeArquivo ? ` (arquivo ${a.nomeArquivo})` : "";

  if (ehCancelado(a)) return explicarCancelado(a);

  if (a.origem === "fila_cte" || a.origem === "fila_fatura") {
    if (tela === "auditoria") {
      return `ainda não foi ${flex(a, "integrado", "integrada")}: está na Integração de Documentos${arquivo}`;
    }
    if (motivos.length) return `está na fila de integração, mas ${motivos.join("; ")}`;
    return `está na fila de integração${arquivo}, mas não aparece com os filtros atuais desta tela`;
  }

  if (tela === "integracao") {
    const onde = a.origem === "fatura" ? "Conferência de Fatura" : "Auditoria de CT-e";
    const base = `já foi ${flex(a, "integrado", "integrada")}${situacao ? ` (situação ${situacao})` : ""}: consulte na ${onde}`;
    return a.foraEmbarcador ? `${base}; ${motivosDeFiltro({ foraEmbarcador: true, embarcador: a.embarcador })[0]}` : base;
  }

  if (motivos.length) return motivos.join("; ");
  return `existe${situacao ? ` (situação ${situacao})` : ""}, mas não aparece com os filtros atuais desta tela`;
}

export function montarAviso({ tipo, tela, termo, achados }) {
  const lista = Array.isArray(achados) ? achados : [];
  if (!lista.length) return null;

  const rotulo = tipo === "fatura" ? "a fatura" : "o CT-e";
  const termoTexto = String(termo || "").trim();
  // Só cancelados: o título diz isso de cara, em vez de "fora do que a tela mostra".
  const soCancelados = lista.every(ehCancelado);
  const titulo = soCancelados
    ? ehChave(termoTexto)
      ? `O CT-e com essa chave foi cancelado na SEFAZ:`
      : `O CT-e ${termoTexto} foi cancelado na SEFAZ:`
    : ehChave(termoTexto)
      ? `Encontramos ${rotulo} com essa chave fora do que esta tela está mostrando:`
      : `Encontramos ${rotulo} ${termoTexto} fora do que esta tela está mostrando:`;

  const linhas = lista.slice(0, MAX_LINHAS).map((a) => `${descreverDocumento(a)}: ${explicar(a, tela)}.`);
  const resto = lista.length - MAX_LINHAS;
  if (resto > 0) linhas.push(`E mais ${resto} documento(s) com o mesmo número.`);

  return { titulo, linhas, cancelado: lista.some(ehCancelado) };
}
